import type { KYCStatus } from '~/api/KYCStatus';
import type {
  MembershipCardUpdatedPayload,
  MembershipCardUpdateFailedPayload,
  MembershipCardUpdateViewedPayload,
} from '~/modules/membership/domain/cardChange';
import type { MembershipTierHintViewedPayload } from '~/modules/membership/domain/cart';
import type { MembershipEntryPayload } from '~/modules/membership/domain/clubEntry';
import type {
  MembershipCancelPayload,
  MembershipPlanChangeKeptPayload,
  MembershipPlanChangePayload,
} from '~/modules/membership/domain/manage';
import type {
  MembershipBuyClickedPayload,
  MembershipPlanEventPayload,
  MembershipPurchaseFailedPayload,
  MembershipPurchaseSucceededPayload,
} from '~/modules/membership/domain/purchase';
import type { MembershipPageViewedPayload } from '~/modules/membership/domain/view';
import type { CartItem, FirebaseCartItem } from '~/stores/CartStore';
import type { Rate } from '~/stores/CatalogStore';
import type { Product } from '~/stores/CategoriesStore';
import type { ApplyPromocodeCallerSource } from '~/stores/PromotionsStore/interfaces';

import type { ProductSourceType } from './sources';

/**
 * Cart mutation reported to BI. `source*` fields attribute the change to the
 * UI surface it started from, so a carousel can be told from a category grid.
 */
export interface CartItemChangePayload {
  product_id: number;
  category_id?: number | null;
  lvl1_category_id: undefined;
  lvl2_category_id?: number | null;
  /** Type of the surface the change originated from */
  source: ProductSourceType;
  /** Identifies the specific surface element; empty when it has no id */
  source_id: string;
  /** Name of the specific surface element; empty when it has no name */
  source_name: string;
  quantity: number;
  cart_id: undefined;
  products_amount: number;
  items_amount: number;
  price: string | number;
  final_price: string | number;
  eta_min: number;
  eta_max: number;
  delivery_fee: string | number;
  threshold: string | number;
  is_surger: boolean;
}

export type ProductSnippetItem = FirebaseCartItem;

export interface ProductSnippetListPayload {
  item_list_id: string;
  item_list_name: string;
  items: ProductSnippetItem[];
}

/** Single order line in the shape TikTok's `contents` array expects */
export interface TikTokContent {
  content_id: string;
  content_type: 'product';
  content_name: string;
  price: number;
  quantity: number;
}

/**
 * Data the TikTok first-purchase tag reads in GTM.
 *
 * Pushed under a single `tiktok` key rather than spread across the data layer:
 * the layer is shared with Yandex Metrika and every other tag in the container,
 * and GTM keeps pushed keys readable for the rest of the page's life.
 */
export interface TikTokFirstPurchaseData {
  /**
   * Order UUID, not the display number. Also sent to Meta Pixel and GA4;
   * server-side events must use the same value for deduplication to work.
   */
  order_id: string;
  /** SHA-256 hex of the trimmed, lowercased email; empty when unknown */
  hashed_email: string;
  /** SHA-256 hex of the E.164 phone; empty when unknown */
  hashed_phone: string;
  /** SHA-256 hex of our customer id; empty when unknown */
  hashed_external_id: string;
  /** Every order line, for tags mapping `contents` from a single variable */
  contents: TikTokContent[];
  /** First order line, for tags built around the scalar variables */
  content_id: string;
  content_type: 'product';
  content_name: string;
  content_price: number;
  content_quantity: number;
  /** Order total actually paid */
  value: number;
  currency: string;
}

/** Data layer entry carrying the TikTok first-purchase payload */
export interface TikTokFirstPurchasePayload {
  tiktok: TikTokFirstPurchaseData;
}

export enum AnalyticsEvent {
  // Firebase GA4 standard events (from analyticsEventsEmitter)
  VIEW_CART = 'view_cart',
  VIEW_ITEM = 'view_item',
  PRODUCT_SNIPPET_CLICKED = 'product_snippet_clicked',
  LOGIN_SCREEN_SHOWN = 'login_screen_shown',
  ONBOARDING_COMPLETE = 'onboarding_complete',
  ONBOARDING_SUBSCRIBE_DIALOG_SHOWN = 'onboarding_subscribe_dialog_shown',
  ONBOARDING_SUBSCRIBE_DIALOG_CLICKED = 'onboarding_subscribe_dialog_clicked',
  CATALOG_SEARCH_VISITED = 'catalog_search_visited',
  CATALOG_SEARCH_CLICKED = 'catalog_search_clicked',
  CATALOG_CATEGORY_VISITED = 'catalog_category_visited',
  CATALOG_CATEGORY_CLICKED = 'catalog_category_clicked',

  // Common analytics events (from analyticsEventsEmitter COMMON_ANALYTICS_*)
  CATALOG_CATEGORY_CLICKED_BI = 'Catalog: category clicked',
  CATALOG_CATEGORY_VISITED_BI = 'Catalog: category visited',
  CATALOG_PRODUCT_CLICKED = 'Catalog: product page clicked',
  CATALOG_PRODUCT_VISITED = 'Catalog: product page visited',
  CATALOG_SEARCH_CLICKED_BI = 'Catalog: search clicked',
  CATALOG_SEARCH_VISITED_BI = 'Catalog: search visited',
  LOGIN_SCREEN_SHOWN_BI = 'Login: screen shown',
  VIEW_CART_BI = 'Purchase: cart viewed',
  ONBOARDING_COMPLETE_BI = 'Onboarding: complete',
  ONBOARDING_SUBSCRIBE_DIALOG_SHOWN_BI = 'Onboarding: subscribe dialog shown',
  ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI = 'Onboarding: subscribe dialog clicked',
  CART_CHANGED = 'Purchase: cart changed',
  REFERRAL_PROGRAM_OPEN = 'Referral program: Open',
  REFERRAL_PROGRAM_SHARE = 'Referral program: Share',
  RATE_PRODUCT_DETAILED = 'Rate: product detailed',
  KYC_VERIFICATION_STATUS = 'KYC: verification status',

  // Events from mainStore.sendAnalytics()
  PURCHASE_COMPLETED = 'Purchase: completed',
  PURCHASE_PAYMENT_STARTED = 'Purchase: payment started',
  PURCHASE_CART_DELETED = 'Purchase: cart deleted',
  PURCHASE_START_CHECKOUT = 'Purchase: start checkout',
  PURCHASE_PROMOCODE_ENTERED = 'Purchase: promocode entered',
  PURCHASE_PRODUCT_ADDED_TO_CART = 'Purchase: product added to cart',
  PURCHASE_PRODUCT_REMOVED_FROM_CART = 'Purchase: product removed from cart',
  DELIVERY_INSTRUCTIONS = 'Delivery instructions',
  GENERAL_ETA_CHANGED = 'General: eta changed',
  SUBSCRIBE_CHANGE_SETTINGS = 'Subscribe: change settings',
  CATALOG_SEARCHED = 'Catalog: searched',
  CATALOG_SEARCH_IDEA_CLICKED = 'Catalog: search idea clicked',
  REORDER_BUTTON_CLICKED = 'Reorder button clicked',
  ORDER_PAGE_VIEW = 'Order page view',
  CHECK_BUTTON_CLICKED = 'Check button clicked',
  CONTACT_SUPPORT_BUTTON_CLICKED = 'Contact support button clicked',
  INVITE_FRIENDS_NOW = 'Invite friends now',
  REFERRAL_PROGRAM_COPY_CODE = 'Referral program: Copy code',
  REFUND_BUTTON_CLICKED = 'Refund: button clicked',
  REFUND_TYPE_SELECTED = 'Refund: type selected',
  REFUND_COMMENT_SUBMITTED = 'Refund: comment submitted',
  REFUND_REQUEST_SUBMITTED = 'Refund: request submitted',
  REFUND_SUCCESS = 'Refund: success',
  REFUND_FAIL = 'Refund: fail',

  // Memberships
  MEMBERSHIP_PAGE_VIEWED = 'Membership: Page Viewed',
  MEMBERSHIP_CHARGES_EXPANDED = 'Membership: Charges Expanded',
  MEMBERSHIP_BUY_CLICKED = 'Membership: Buy Clicked',
  MEMBERSHIP_CHECKOUT_VIEWED = 'Membership: Checkout Viewed',
  MEMBERSHIP_CARD_SUBMITTED = 'Membership: Card Submitted',
  MEMBERSHIP_3DS_SHOWN = 'Membership: 3DS Shown',
  MEMBERSHIP_PURCHASE_SUCCEEDED = 'Membership: Purchase Succeeded',
  MEMBERSHIP_PURCHASE_FAILED = 'Membership: Purchase Failed',
  MEMBERSHIP_CANCEL_CLICKED = 'Membership: Cancel Clicked',
  MEMBERSHIP_CANCELLED = 'Membership: Cancelled',
  MEMBERSHIP_PLAN_CHANGE_CLICKED = 'Membership: Plan Change Clicked',
  MEMBERSHIP_PLAN_CHANGED = 'Membership: Plan Changed',
  MEMBERSHIP_PLAN_CHANGE_KEPT = 'Membership: Plan Change Kept',
  MEMBERSHIP_CARD_UPDATE_VIEWED = 'Membership: Card Update Viewed',
  MEMBERSHIP_CARD_UPDATED = 'Membership: Card Updated',
  MEMBERSHIP_CARD_UPDATE_FAILED = 'Membership: Card Update Failed',
  /**
   * Once per mount of a club entry point (home banner, profile card, cart and
   * checkout rows, club page hero) when it shows; `source` names it.
   */
  MEMBERSHIP_ENTRY_VIEWED = 'Membership: Entry Viewed',
  /** A club entry point was clicked: home, cart, checkout, profile, menu, footer or the club page. */
  MEMBERSHIP_ENTRY_CLICKED = 'Membership: Entry Clicked',
  /**
   * Once per mount of the cart banner and step: reaching a new discount step
   * (another `percent`) sends it again, a changing missing amount does not.
   */
  MEMBERSHIP_TIER_HINT_VIEWED = 'Membership: Tier Hint Viewed',

  // Meta Pixel events
  PIXEL_PURCHASE = 'pixel:Purchase',
  PIXEL_ADD_TO_CART = 'pixel:AddToCart',
  PIXEL_INITIATE_CHECKOUT = 'pixel:InitiateCheckout',
  PIXEL_VIEW_CONTENT = 'pixel:ViewContent',
  PIXEL_PAGE_VIEW = 'pixel:PageView',

  // Direct sendToRN events (Firebase/RN analytics)
  ADD_TO_CART = 'add_to_cart',
  REMOVE_FROM_CART = 'remove_from_cart',
  PRODUCT_ADDED_TO_FAVORITES = 'General: product added to favorites',
  ADD_TO_WISHLIST = 'add_to_wishlist',
  SELECT_PROMOTION = 'select_promotion',
  AF_FIRST_PURCHASE = 'af_first_purchase',
  FIRST_OPEN = 'first_open',
  FIRST_PURCHASE = 'first_purchase',
  TIKTOK_FIRST_PURCHASE = 'tiktok_first_purchase',
  PURCHASE = 'purchase',
  ADD_PAYMENT_INFO = 'add_payment_info',
  BEGIN_CHECKOUT = 'begin_checkout',
  CATALOG_HOME_PAGE_VISITED = 'Catalog: home page visited',
  DELIVERY_METHOD_SELECT_TIME = 'Delivery method: Select time',
  DELIVERY_METHOD_SELECTED = 'Delivery method: Selected',
  PURCHASE_PAYMENT_METHOD_FILLED_IN = 'Purchase: payment method filled in',
  LOGIN_PHONE_ENTERED = 'Login: phone entered',
  GENERAL_ADDRESS_CHANGED = 'General: address changed',
  ONBOARDING_WAREHOUSE_SELECTED = 'Onboarding: warehouse selected',
  EVENT_APP_FEEDBACK = 'Event: App feedback',
  SEARCH = 'search',
  LOGIN_CODE_ENTERED = 'Login: code entered',
  LOGIN_CODE_ENTERED_FIREBASE = 'login_code_entered',
  LOGIN = 'login',
  RATE_ORDER_GENERAL = 'Rate: order general',
  RATE_ORDER_GENERAL_FIREBASE = 'rate_order_general',
  RATE_ORDER_DETAILED = 'Rate: order detailed',
  RATE_ORDER_DETAILED_FIREBASE = 'rate_order_detailed',
  CATALOG_CATEGORY_SCROLLED = 'Catalog: category scrolled',
  CATALOG_CATEGORY_SCROLLED_FIREBASE = 'catalog_category_scrolled',
  PROFILE_DELETED = 'profile_deleted',

  // Yandex Metrika-specific events
  CLICK_BUY = 'click_buy',
  VIEW_OFFER = 'view_offer',
  PAGE_VIEW_YM = 'Page view: yandex metrika',

  // Phase 6: New analytics events
  PAGE_VIEW_BI = 'Page view',
  PAYMENT_METHOD_SELECTED = 'Payment method: selected',
  ORDER_STATUS_CHANGED = 'Order: status changed',
  SIGN_UP = 'sign_up',

  // Batch event
  CATALOG_PRODUCT_SNIPPET_SHOWN = 'Catalog: product snippet shown',
}

export interface EventPayloadMap {
  [AnalyticsEvent.VIEW_CART]: Record<string, unknown>;
  [AnalyticsEvent.VIEW_ITEM]: {
    currency: string;
    value: number;
    items: Record<string, unknown>[];
  };
  [AnalyticsEvent.PRODUCT_SNIPPET_CLICKED]: ProductSnippetListPayload;
  [AnalyticsEvent.LOGIN_SCREEN_SHOWN]: undefined;
  [AnalyticsEvent.ONBOARDING_COMPLETE]: Record<string, unknown>;
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN]: Record<string, unknown>;
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_SEARCH_VISITED]: { source: string };
  [AnalyticsEvent.CATALOG_SEARCH_CLICKED]: { source: string };
  [AnalyticsEvent.CATALOG_CATEGORY_VISITED]: {
    id: string | number;
    source: string;
  };
  [AnalyticsEvent.CATALOG_CATEGORY_CLICKED]: {
    id: string | number;
    source: string;
  };
  [AnalyticsEvent.CATALOG_CATEGORY_CLICKED_BI]: {
    id: number | string;
    source: string;
  };
  [AnalyticsEvent.CATALOG_CATEGORY_VISITED_BI]: {
    id: number | string;
    source: string;
  };
  [AnalyticsEvent.CATALOG_PRODUCT_CLICKED]: Product | CartItem;
  [AnalyticsEvent.CATALOG_PRODUCT_VISITED]: Product | CartItem;
  [AnalyticsEvent.CATALOG_SEARCH_CLICKED_BI]: { source: string };
  [AnalyticsEvent.CATALOG_SEARCH_VISITED_BI]: { source: string };
  [AnalyticsEvent.LOGIN_SCREEN_SHOWN_BI]: undefined;
  [AnalyticsEvent.VIEW_CART_BI]: Record<string, unknown>;
  [AnalyticsEvent.ONBOARDING_COMPLETE_BI]: Record<string, unknown>;
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN_BI]: Record<
    string,
    unknown
  >;
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI]: Record<
    string,
    unknown
  >;
  [AnalyticsEvent.CART_CHANGED]: Record<string, unknown>;
  [AnalyticsEvent.REFERRAL_PROGRAM_OPEN]: undefined;
  [AnalyticsEvent.REFERRAL_PROGRAM_SHARE]: undefined;
  [AnalyticsEvent.RATE_PRODUCT_DETAILED]: {
    productId: number;
    mark: Rate;
    content: string | null;
  };
  [AnalyticsEvent.KYC_VERIFICATION_STATUS]: { status: KYCStatus };
  [AnalyticsEvent.PURCHASE_COMPLETED]: Record<string, unknown>;
  [AnalyticsEvent.PURCHASE_PAYMENT_STARTED]: {
    order_id: string;
    price: string | number;
    final_price: string | number;
    items_amount: number;
    products_amount: number;
    currency: string;
    payment_method: string;
    payment_system: string;
  };
  [AnalyticsEvent.PURCHASE_CART_DELETED]: Record<string, unknown>;
  [AnalyticsEvent.PURCHASE_START_CHECKOUT]: Record<string, unknown>;
  [AnalyticsEvent.PURCHASE_PROMOCODE_ENTERED]: {
    /** Entered promocode value */
    code: string;
    /** Real apply outcome — true on success, false on any rejection */
    success: boolean;
    /** Stable machine-readable rejection reason (server err_code or client code); omitted on success */
    error_code?: string;
    /** Entry surface the apply originated from */
    source?: ApplyPromocodeCallerSource;
    discount?: string | number;
    type?: string;
    products_amount?: number;
    items_amount?: number;
    price?: string | number;
    final_price?: string | number;
    eta_min?: number;
    eta_max?: number;
    delivery_fee?: string | number;
    threshold?: string | number;
    is_surger?: boolean;
  };
  [AnalyticsEvent.PURCHASE_PRODUCT_ADDED_TO_CART]: CartItemChangePayload;
  [AnalyticsEvent.PURCHASE_PRODUCT_REMOVED_FROM_CART]: CartItemChangePayload;
  [AnalyticsEvent.DELIVERY_INSTRUCTIONS]: Record<string, unknown>;
  [AnalyticsEvent.GENERAL_ETA_CHANGED]: Record<string, unknown>;
  [AnalyticsEvent.SUBSCRIBE_CHANGE_SETTINGS]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_SEARCHED]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_SEARCH_IDEA_CLICKED]: Record<string, unknown>;
  [AnalyticsEvent.REORDER_BUTTON_CLICKED]: Record<string, unknown>;
  [AnalyticsEvent.ORDER_PAGE_VIEW]: Record<string, unknown>;
  [AnalyticsEvent.CHECK_BUTTON_CLICKED]: Record<string, unknown>;
  [AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED]: Record<string, unknown>;
  [AnalyticsEvent.INVITE_FRIENDS_NOW]: Record<string, unknown>;
  [AnalyticsEvent.REFERRAL_PROGRAM_COPY_CODE]: Record<string, unknown>;
  [AnalyticsEvent.PIXEL_PURCHASE]: Record<string, unknown>;
  [AnalyticsEvent.PIXEL_ADD_TO_CART]: Record<string, unknown>;
  [AnalyticsEvent.PIXEL_INITIATE_CHECKOUT]: Record<string, unknown>;
  [AnalyticsEvent.PIXEL_VIEW_CONTENT]: Record<string, unknown>;
  [AnalyticsEvent.PIXEL_PAGE_VIEW]: Record<string, unknown>;
  [AnalyticsEvent.ADD_TO_CART]: Record<string, unknown>;
  [AnalyticsEvent.REMOVE_FROM_CART]: Record<string, unknown>;
  [AnalyticsEvent.PRODUCT_ADDED_TO_FAVORITES]: Record<string, unknown>;
  [AnalyticsEvent.ADD_TO_WISHLIST]: Record<string, unknown>;
  [AnalyticsEvent.SELECT_PROMOTION]: Record<string, unknown>;
  [AnalyticsEvent.AF_FIRST_PURCHASE]: Record<string, unknown>;
  [AnalyticsEvent.FIRST_OPEN]: Record<string, unknown>;
  [AnalyticsEvent.FIRST_PURCHASE]: Record<string, unknown>;
  [AnalyticsEvent.TIKTOK_FIRST_PURCHASE]: TikTokFirstPurchasePayload;
  [AnalyticsEvent.PURCHASE]: Record<string, unknown>;
  [AnalyticsEvent.ADD_PAYMENT_INFO]: Record<string, unknown>;
  [AnalyticsEvent.BEGIN_CHECKOUT]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_HOME_PAGE_VISITED]: Record<string, unknown>;
  [AnalyticsEvent.DELIVERY_METHOD_SELECT_TIME]: Record<string, unknown>;
  [AnalyticsEvent.DELIVERY_METHOD_SELECTED]: Record<string, unknown>;
  [AnalyticsEvent.PURCHASE_PAYMENT_METHOD_FILLED_IN]: Record<string, unknown>;
  [AnalyticsEvent.LOGIN_PHONE_ENTERED]: Record<string, unknown>;
  [AnalyticsEvent.GENERAL_ADDRESS_CHANGED]: Record<string, unknown>;
  [AnalyticsEvent.ONBOARDING_WAREHOUSE_SELECTED]: Record<string, unknown>;
  [AnalyticsEvent.EVENT_APP_FEEDBACK]: Record<string, unknown>;
  [AnalyticsEvent.SEARCH]: Record<string, unknown>;
  [AnalyticsEvent.LOGIN_CODE_ENTERED]: Record<string, unknown>;
  [AnalyticsEvent.LOGIN_CODE_ENTERED_FIREBASE]: Record<string, unknown>;
  [AnalyticsEvent.LOGIN]: Record<string, unknown>;
  [AnalyticsEvent.RATE_ORDER_GENERAL]: Record<string, unknown>;
  [AnalyticsEvent.RATE_ORDER_GENERAL_FIREBASE]: Record<string, unknown>;
  [AnalyticsEvent.RATE_ORDER_DETAILED]: Record<string, unknown>;
  [AnalyticsEvent.RATE_ORDER_DETAILED_FIREBASE]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_CATEGORY_SCROLLED]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_CATEGORY_SCROLLED_FIREBASE]: Record<string, unknown>;
  [AnalyticsEvent.PROFILE_DELETED]: Record<string, unknown>;
  [AnalyticsEvent.CLICK_BUY]: Record<string, unknown>;
  [AnalyticsEvent.PAGE_VIEW_YM]: { url: string };
  [AnalyticsEvent.VIEW_OFFER]: {
    currency: string;
    value: number;
    items: Record<string, unknown>[];
  };
  [AnalyticsEvent.PAGE_VIEW_BI]: { path: string; title?: string };
  [AnalyticsEvent.PAYMENT_METHOD_SELECTED]: Record<string, unknown>;
  [AnalyticsEvent.ORDER_STATUS_CHANGED]: Record<string, unknown>;
  [AnalyticsEvent.SIGN_UP]: Record<string, unknown>;
  [AnalyticsEvent.CATALOG_PRODUCT_SNIPPET_SHOWN]: ProductSnippetItem;
  [AnalyticsEvent.REFUND_BUTTON_CLICKED]: { orderId: string };
  [AnalyticsEvent.REFUND_TYPE_SELECTED]: {
    orderId: string;
    type: 'all' | 'specific';
    itemsCount: number;
  };
  [AnalyticsEvent.REFUND_COMMENT_SUBMITTED]: {
    orderId: string;
    commentLength: number;
  };
  [AnalyticsEvent.REFUND_REQUEST_SUBMITTED]: {
    orderId: string;
    type: 'all' | 'specific';
    itemsCount: number;
    reasonCode: string;
  };
  [AnalyticsEvent.REFUND_SUCCESS]: { orderId: string };
  [AnalyticsEvent.REFUND_FAIL]: { orderId: string; error: string };
  [AnalyticsEvent.MEMBERSHIP_PAGE_VIEWED]: MembershipPageViewedPayload;
  [AnalyticsEvent.MEMBERSHIP_CHARGES_EXPANDED]: {
    /** Page of the payment history being requested. */
    page: number;
  };
  [AnalyticsEvent.MEMBERSHIP_BUY_CLICKED]: MembershipBuyClickedPayload;
  [AnalyticsEvent.MEMBERSHIP_CHECKOUT_VIEWED]: MembershipPlanEventPayload;
  [AnalyticsEvent.MEMBERSHIP_CARD_SUBMITTED]: MembershipPlanEventPayload;
  [AnalyticsEvent.MEMBERSHIP_3DS_SHOWN]: MembershipPlanEventPayload;
  [AnalyticsEvent.MEMBERSHIP_PURCHASE_SUCCEEDED]: MembershipPurchaseSucceededPayload;
  [AnalyticsEvent.MEMBERSHIP_PURCHASE_FAILED]: MembershipPurchaseFailedPayload;
  [AnalyticsEvent.MEMBERSHIP_CANCEL_CLICKED]: MembershipCancelPayload;
  [AnalyticsEvent.MEMBERSHIP_CANCELLED]: MembershipCancelPayload;
  [AnalyticsEvent.MEMBERSHIP_PLAN_CHANGE_CLICKED]: MembershipPlanChangePayload;
  [AnalyticsEvent.MEMBERSHIP_PLAN_CHANGED]: MembershipPlanChangePayload;
  [AnalyticsEvent.MEMBERSHIP_PLAN_CHANGE_KEPT]: MembershipPlanChangeKeptPayload;
  [AnalyticsEvent.MEMBERSHIP_CARD_UPDATE_VIEWED]: MembershipCardUpdateViewedPayload;
  [AnalyticsEvent.MEMBERSHIP_CARD_UPDATED]: MembershipCardUpdatedPayload;
  [AnalyticsEvent.MEMBERSHIP_CARD_UPDATE_FAILED]: MembershipCardUpdateFailedPayload;
  [AnalyticsEvent.MEMBERSHIP_ENTRY_VIEWED]: MembershipEntryPayload;
  [AnalyticsEvent.MEMBERSHIP_ENTRY_CLICKED]: MembershipEntryPayload;
  [AnalyticsEvent.MEMBERSHIP_TIER_HINT_VIEWED]: MembershipTierHintViewedPayload;
}
